import { useState } from 'react';
import Switch from '@/components/base/Switch';
import type { LaundryItem, ServicePrice } from '@/mocks/items';

type ServiceKey = 'washOnly' | 'ironOnly' | 'washIron';

const serviceHeaders: Array<{ key: ServiceKey; label: string }> = [
  { key: 'washOnly', label: 'غسيل فقط' },
  { key: 'ironOnly', label: 'كوي فقط' },
  { key: 'washIron', label: 'غسيل وكوي' },
];

interface PriceCellProps {
  value: number;
  enabled: boolean;
  onSave: (value: number) => void;
}

function PriceCell({ value, enabled, onSave }: PriceCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  if (!enabled) {
    return (
      <td className="border border-background-100 px-3 py-2 text-center">
        <span className="text-[13px] text-foreground-300 line-through">—</span>
      </td>
    );
  }

  if (editing) {
    return (
      <td className="border border-background-100 px-2 py-1.5 text-center">
        <input
          type="number"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => {
            const parsed = Number(draft);
            if (!Number.isNaN(parsed) && parsed > 0) onSave(parsed);
            setEditing(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
            if (event.key === 'Escape') setEditing(false);
          }}
          autoFocus
          className="w-full rounded border border-secondary-400 bg-secondary-50 px-2 py-1 text-center text-[13px] font-semibold text-foreground-950 focus:outline-none focus:ring-1 focus:ring-secondary-300"
        />
      </td>
    );
  }

  return (
    <td
      className="cursor-pointer border border-background-100 px-3 py-2 text-center transition-colors hover:bg-secondary-50"
      onClick={() => {
        setDraft(String(value));
        setEditing(true);
      }}
      title="اضغط لتعديل السعر"
    >
      <span className="text-[13px] font-semibold text-foreground-950">{value}</span>
      <span className="me-0.5 text-[11px] text-foreground-400">ر.س</span>
    </td>
  );
}

interface ItemCardProps {
  item: LaundryItem;
  onToggleActive: () => void;
  onToggleService: (service: ServiceKey, enabled: boolean) => void;
  onPriceChange: (service: ServiceKey, tier: 'normal' | 'urgent', price: number) => void;
  onEdit: () => void;
  onDelete: () => void;
}

export default function ItemCard({
  item,
  onToggleActive,
  onToggleService,
  onPriceChange,
  onEdit,
  onDelete,
}: ItemCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div
      className={[
        'rounded-card border border-background-200 bg-white p-4 shadow-card transition-opacity duration-300',
        item.active ? 'opacity-100' : 'opacity-50',
      ].join(' ')}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 overflow-hidden rounded-[10px] border border-background-100 bg-background-50 shrink-0">
            <img
              src={item.image}
              alt={item.name}
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div>
            <h3 className="font-semibold text-[16px] text-foreground-950 leading-tight">{item.name}</h3>
            {!item.active && (
              <span className="mt-1 inline-flex items-center rounded-full bg-danger-50 px-2.5 py-0.5 text-[11px] font-medium text-danger-600">
                معطّلة
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Switch checked={item.active} onChange={onToggleActive} size="sm" />
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-700"
              aria-label="خيارات"
            >
              <i className="ri-more-2-fill text-lg" aria-hidden="true"></i>
            </button>
            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setMenuOpen(false)}
                  aria-hidden="true"
                ></div>
                <div className="dc-fade-up absolute left-0 z-20 mt-1 w-[140px] rounded-card border border-background-200 bg-white p-1 shadow-soft">
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onEdit();
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-right text-[13px] text-foreground-700 transition-colors hover:bg-background-50"
                  >
                    <i className="ri-pencil-line text-base text-secondary-500" aria-hidden="true"></i>
                    تعديل
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onDelete();
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-right text-[13px] text-danger-600 transition-colors hover:bg-danger-50"
                  >
                    <i className="ri-delete-bin-line text-base" aria-hidden="true"></i>
                    حذف
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-background-200">
        <table className="w-full border-collapse text-right">
          <thead>
            <tr className="bg-background-50">
              <th className="border border-background-100 px-3 py-2 text-right text-[11px] font-semibold text-foreground-500">
                الأولوية
              </th>
              {serviceHeaders.map(({ key, label }) => (
                <th key={key} className="border border-background-100 px-3 py-2 text-center text-[11px] font-semibold text-foreground-500">
                  <div className="flex items-center justify-center gap-1.5">
                    <Switch
                      checked={(item[key] as ServicePrice).enabled}
                      onChange={(checked) => onToggleService(key, checked)}
                      size="sm"
                    />
                    {label}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(['normal', 'urgent'] as const).map((tier) => (
              <tr key={tier}>
                <td className="border border-background-100 px-3 py-2">
                  <span
                    className={[
                      'text-[12px] font-medium',
                      tier === 'urgent' ? 'text-accent-600' : 'text-foreground-600',
                    ].join(' ')}
                  >
                    {tier === 'normal' ? 'عادي' : 'مستعجل'}
                  </span>
                </td>
                {serviceHeaders.map(({ key }) => (
                  <PriceCell
                    key={key}
                    value={(item[key] as ServicePrice)[tier]}
                    enabled={(item[key] as ServicePrice).enabled}
                    onSave={(price) => onPriceChange(key, tier, price)}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}